import { EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ThemeService } from '../services/theme.service';
import { AnnouncementService } from '../services/announcement.service';
import { LoggerService } from '../services/logger.service';

/**
 * Font-family validation: allows alphanumeric, spaces, commas, hyphens, single quotes, periods.
 * Rejects semicolons, braces, and other CSS-injection characters.
 * Hyphen placed at the end of the character class to avoid ambiguous range interpretation.
 */
const FONT_FAMILY_PATTERN = /^[a-zA-Z0-9\s,.''-]+$/;

/** Allowed CSS custom property keys for custom theme colors */
export const ALLOWED_THEME_KEYS = new Set([
  'primaryColor', 'secondaryColor', 'accentColor',
  'backgroundColor', 'surfaceColor', 'textColor',
  'borderColor', 'mutedColor', 'destructiveColor',
  'chartColor1', 'chartColor2', 'chartColor3', 'chartColor4',
]);

/** Maximum custom theme entries to prevent main-thread blocking via localStorage tampering */
const MAX_THEME_ENTRIES = 50;

/**
 * Initialize custom theme properties from localStorage.
 *
 * Dark/light mode is NOT handled here — ThemeService reads the 'theme'
 * key in its constructor and applies .dark to <html> via an effect().
 * This initializer handles supplementary styling only: custom colors
 * and font-family.
 */
export function initializeTheme(logger: LoggerService, doc: Document): () => Promise<void> {
  return () => new Promise((resolve) => {
      const storage = doc.defaultView?.localStorage;
      if (!storage) { resolve(); return; }

      // Load custom theme if active
      const customThemeActive = storage.getItem('customThemeActive');
      if (customThemeActive === 'true') {
        const customColors = storage.getItem('customThemeColors');
        if (customColors) {
          try {
            const colors = JSON.parse(customColors);
            applyCustomTheme(colors, doc, logger);
          } catch (error) {
            logger.error('Failed to load custom theme:', { error });
          }
        }
      }

      // Load saved font (validated against safe pattern)
      const savedFont = storage.getItem('selectedFont');
      if (savedFont && FONT_FAMILY_PATTERN.test(savedFont)) {
        doc.documentElement.style.setProperty('--font-family', savedFont);
      } else if (savedFont) {
        logger.warn('Rejected invalid font-family from localStorage', {
          component: 'AppInitializer',
          font: savedFont.substring(0, 50),
        });
      }

      resolve();
    });
}

/**
 * Initialize accessibility features.
 * Uses a DOM data attribute as the idempotency guard so it survives
 * HMR (prevents duplicate listeners) while resetting cleanly across
 * TestBed bootstraps (each gets a fresh Document).
 */
export function initializeAccessibility(
  announcementService: AnnouncementService,
  themeService: ThemeService,
  doc: Document,
): () => Promise<void> {
  return () => new Promise((resolve) => {
      if (!doc.defaultView || doc.body.hasAttribute('data-a11y-initialized')) { resolve(); return; }
      doc.body.setAttribute('data-a11y-initialized', 'true');

      setupKeyboardShortcuts(doc, themeService);
      initializeFocusManagement(doc);
      announcementService.announce('Application loaded', 'polite');

      resolve();
    });
}

/**
 * Setup keyboard shortcuts for accessibility navigation and quick actions.
 */
function setupKeyboardShortcuts(doc: Document, themeService: ThemeService): void {
  doc.addEventListener('keydown', (event) => {
    // Alt + N: Skip to navigation
    if (event.altKey && event.key === 'n') {
      event.preventDefault();
      const nav = doc.querySelector('#main-navigation') as HTMLElement;
      nav?.focus();
    }

    // Alt + C: Skip to content
    if (event.altKey && event.key === 'c') {
      event.preventDefault();
      const content = doc.querySelector('#main-content') as HTMLElement;
      content?.focus();
    }

    // Alt + S: Focus search
    if (event.altKey && event.key === 's') {
      event.preventDefault();
      const search = doc.querySelector('#search') as HTMLElement;
      search?.focus();
    }

    // Alt + T: Toggle theme via ThemeService (persists and syncs signal state)
    if (event.altKey && event.key === 't') {
      event.preventDefault();
      themeService.toggleDark();
    }
  });
}

/**
 * Initialize focus management — adds keyboard-navigation class on Tab,
 * removes it on mouse click.
 */
function initializeFocusManagement(doc: Document): void {
  doc.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') {
      doc.body.classList.add('keyboard-navigation');
    }
  });

  doc.addEventListener('mousedown', () => {
    doc.body.classList.remove('keyboard-navigation');
  });
}

/**
 * Apply custom theme colors to CSS custom properties.
 * Keys are validated against an allowlist; values are coerced through hexToHSL.
 */
function applyCustomTheme(colors: Record<string, string>, doc: Document, logger: LoggerService): void {
  if (typeof colors !== 'object' || colors === null) return;

  const entries = Object.entries(colors);
  if (entries.length > MAX_THEME_ENTRIES) {
    logger.warn('Custom theme has too many entries, skipping', {
      component: 'AppInitializer',
      count: entries.length,
      max: MAX_THEME_ENTRIES,
    });
    return;
  }

  const root = doc.documentElement;
  for (const [key, value] of entries) {
    if (!ALLOWED_THEME_KEYS.has(key)) continue;
    if (typeof value !== 'string') continue;
    const hsl = hexToHSL(value);
    root.style.setProperty(`--${camelToKebab(key)}`, hsl);
  }
}

/**
 * Convert hex color to HSL string.
 * Returns "0 0% 0%" for invalid input (safe CSS fallback).
 */
export function hexToHSL(hex: string): string {
  hex = hex.replace('#', '');

  // Expand 3-char shorthand (e.g., "f00" → "ff0000")
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) {
    return '0 0% 0%';
  }

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Convert camelCase to kebab-case
 */
function camelToKebab(str: string): string {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * App initializer providers — theme customization and accessibility.
 *
 * Dark/light mode is handled by ThemeService (reads localStorage 'theme' key
 * in constructor). Language initialization is handled by TranslationService
 * constructor. Both are providedIn:'root' and do not need explicit initializers.
 */
export const appInitializerProviders: EnvironmentProviders[] = [
  provideAppInitializer(() => initializeTheme(inject(LoggerService), inject(DOCUMENT))()),
  provideAppInitializer(() =>
    initializeAccessibility(inject(AnnouncementService), inject(ThemeService), inject(DOCUMENT))(),
  ),
];
